import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { logger } from '../services/LoggerService'

// Extend request config to track timing
interface TimedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _startTime?: number;
}

export const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
})

// Token storage keys (versioned to handle auth scheme changes across deployments)
const TOKEN_SCHEMA_VERSION = 'v1'
const ACCESS_TOKEN_KEY = `procenlogs_${TOKEN_SCHEMA_VERSION}_access_token`
const REFRESH_TOKEN_KEY = `procenlogs_${TOKEN_SCHEMA_VERSION}_refresh_token`

// Clean up legacy unversioned keys on load
try {
  const legacyAccess = localStorage.getItem('procenlogs_access_token')
  if (legacyAccess) {
    localStorage.removeItem('procenlogs_access_token')
    localStorage.removeItem('procenlogs_refresh_token')
  }
} catch { /* ignore in SSR/restricted contexts */ }

// In-memory cache for tokens to avoid repeated localStorage reads
let cachedAccessToken: string | null = null
let cachedRefreshToken: string | null = null

// Token management
export const tokenManager = {
  getAccessToken: () => {
    if (cachedAccessToken === null) {
      cachedAccessToken = localStorage.getItem(ACCESS_TOKEN_KEY)
    }
    return cachedAccessToken
  },
  getRefreshToken: () => {
    if (cachedRefreshToken === null) {
      cachedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
    }
    return cachedRefreshToken
  },
  setTokens: (accessToken: string, refreshToken: string) => {
    cachedAccessToken = accessToken
    cachedRefreshToken = refreshToken
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  },
  clearTokens: () => {
    cachedAccessToken = null
    cachedRefreshToken = null
    localStorage.removeItem(ACCESS_TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
  },
}

// Request interceptor to add JWT token and track timing
api.interceptors.request.use(
  (config) => {
    const token = tokenManager.getAccessToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    (config as TimedAxiosRequestConfig)._startTime = Date.now()
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor to handle 401 and refresh token
let isRefreshing = false
let failedQueue: Array<{ resolve: (value?: unknown) => void; reject: (reason?: unknown) => void }> = []

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

api.interceptors.response.use(
  (response) => {
    const config = response.config as TimedAxiosRequestConfig
    const duration = config._startTime ? Date.now() - config._startTime : 0
    logger.logApiCall(
      config.method?.toUpperCase() || 'UNKNOWN',
      config.url || '',
      response.status,
      duration
    )
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as TimedAxiosRequestConfig & { _retry?: boolean }

    if (originalRequest) {
      const duration = originalRequest._startTime ? Date.now() - originalRequest._startTime : 0
      logger.logApiCall(
        originalRequest.method?.toUpperCase() || 'UNKNOWN',
        originalRequest.url || '',
        error.response?.status || 0,
        duration
      )
      logger.error('API request failed', {
        url: originalRequest.url,
        method: originalRequest.method,
        status: error.response?.status,
        statusText: error.response?.statusText,
        errorMessage: error.message,
      })
    }

    const isAuthEndpoint = originalRequest?.url?.includes('/auth/login') ||
                           originalRequest?.url?.includes('/auth/setup')

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            return api(originalRequest)
          })
          .catch((err) => Promise.reject(err))
      }

      originalRequest._retry = true
      isRefreshing = true

      const refreshToken = tokenManager.getRefreshToken()
      if (!refreshToken) {
        tokenManager.clearTokens()
        window.location.href = '/login'
        return Promise.reject(error)
      }

      try {
        const response = await axios.post<{ accessToken: string; refreshToken: string }>('/api/auth/refresh', {
          refreshToken,
        })
        const { accessToken, refreshToken: newRefreshToken } = response.data
        tokenManager.setTokens(accessToken, newRefreshToken)
        processQueue(null, accessToken)
        originalRequest.headers.Authorization = `Bearer ${accessToken}`
        return api(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError as Error, null)
        tokenManager.clearTokens()
        window.location.href = '/login'
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

export default api
